/**
 * <offer-progress> — tracks the live cart against MoreWoof's three stacking offers.
 *
 * Display only. The discounts are Shopify automatic discounts ("FreeShipping100",
 * "Buy Three, Get Third Item At 50% Off"); nothing here creates or applies a discount.
 *
 * The auto-added free gift is excluded from EVERY measure here. It is not
 * merchandise the shopper chose, so counting it would inflate the item count
 * (claiming the 3-item offer is unlocked when only 2 items were bought) and its
 * discount would inflate the reported saving. Thresholds are measured on what
 * the shopper is actually paying for real items, which is the number they read
 * in the cart — the same basis the gift engine uses to decide the $50 tier, so
 * the two can never contradict each other.
 *
 * Reads cart state from the standard Ajax Cart API (/cart.js) and refreshes on the
 * theme's existing pub/sub cart events.
 */
if (!customElements.get('offer-progress')) {
  class OfferProgress extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-fill]');
      this.steps = Array.from(this.querySelectorAll('.mw-offer__step'));
      this.stackText = this.querySelector('[data-stack-text]');

      this.t1 = parseInt(this.dataset.t1, 10) || 0;
      this.t2 = parseInt(this.dataset.t2, 10) || 0;
      this.t3 = parseInt(this.dataset.t3, 10) || 0;

      this.lowest = parseInt(this.dataset.cartLowest, 10) || 0;
      this.pct = parseFloat(this.dataset.tier3Discount) || 50;
      this.discount = parseInt(this.dataset.cartDiscount, 10) || 0;
      this.giftVariantId = String(this.dataset.giftVariant || '');
      this.hasCartData = false;
      this.render(parseInt(this.dataset.cartTotal, 10) || 0, parseInt(this.dataset.cartCount, 10) || 0);

      // The theme publishes 'cart-update' from cart.js, product-form.js and quick-add.
      // Payload shape differs per source, so re-read the cart rather than trusting it.
      this.unsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        [PUB_SUB_EVENTS.cartUpdate, PUB_SUB_EVENTS.quantityUpdate].forEach((evt) => {
          if (evt) this.unsubscribers.push(subscribe(evt, () => this.refresh()));
        });
      }
      // Belt and braces for anything that updates the cart without publishing.
      this.onPageShow = (e) => { if (e.persisted) this.refresh(); };
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      (this.unsubscribers || []).forEach((fn) => fn && fn());
      window.removeEventListener('pageshow', this.onPageShow);
    }

    refresh() {
      fetch(`${window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          if (!cart) return;
          const isGift = (i) => this.giftVariantId && String(i.variant_id) === this.giftVariantId;
          const items = (cart.items || []).filter((i) => !isGift(i));

          // the Buy-3 discount comes off the cheapest unit the shopper chose
          const prices = items.map((i) => i.price).filter((p) => typeof p === 'number');
          this.lowest = prices.length ? Math.min.apply(null, prices) : 0;

          // money actually taken off, ignoring the gift's own 100% allocation
          this.discount = items.reduce(
            (sum, i) => sum + (i.original_line_price - i.final_line_price),
            0
          );

          const total = items.reduce((sum, i) => sum + i.final_line_price, 0);
          const count = items.reduce((sum, i) => sum + i.quantity, 0);

          this.hasCartData = true;
          this.render(total, count);
        })
        .catch(() => {});
    }

    money(cents) {
      const format =
        (window.theme && window.theme.settings && window.theme.settings.money_format) ||
        (window.Shopify && window.Shopify.money_format) ||
        '${{amount}}';
      if (typeof formatMoney === 'function') return formatMoney(cents, format);
      return '$' + (cents / 100).toFixed(2);
    }

    // Thresholds are authored in the shop's base currency, same as free-shipping.js.
    rate() {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    }

    render(totalCents, itemCount) {
      const rate = this.rate();
      const t1 = Math.round(this.t1 * rate);
      const t2 = Math.round(this.t2 * rate);
      const t3 = this.t3;

      const done1 = totalCents >= t1;
      const done2 = totalCents >= t2;
      // Item count only. It used to also unlock on "any discount exists", which
      // breaks once the free gift is live: the gift's own 100% discount would
      // mark the 3-item offer unlocked on a single-item cart.
      const done3 = itemCount >= t3;

      const states = [
        {
          done: done1,
          message: done1 ? this.text('unlocked') : this.highlight(this.text('away'), '[amount]', this.money(t1 - totalCents)),
        },
        {
          done: done2,
          message: done2 ? this.text('unlocked') : this.highlight(this.text('away'), '[amount]', this.money(t2 - totalCents)),
        },
        {
          done: done3,
          message: done3
            ? this.highlight(
                this.text('qtyDone'),
                '[saving]',
                this.money(this.discount > 0 ? this.discount : Math.round(this.lowest * (this.pct / 100)))
              )
            : this.qtyMessage(t3 - itemCount, t3, itemCount),
        },
      ];

      states.forEach((state, i) => {
        const step = this.steps[i];
        if (!step) return;
        step.classList.toggle('is-complete', state.done);
        const status = step.querySelector('[data-status]');
        if (status) status.innerHTML = state.message;
      });

      // Overall completion across the three milestones. Exposed as custom properties so
      // the horizontal rail (desktop) and the vertical connectors (mobile) share one value.
      const p1 = Math.min(totalCents / (t1 || 1), 1);
      const p2 = Math.min(totalCents / (t2 || 1), 1);
      const p3 = done3 ? 1 : Math.min(itemCount / (t3 || 1), 1);
      const f = Math.max(0, Math.min(1, (p1 + p2 + p3) / 3));
      const clamp01 = (n) => Math.max(0, Math.min(1, n));
      this.style.setProperty('--mw-offer-fill', `${f * 100}%`);
      // three nodes = two segments, so the first covers 0-50% of the journey
      // percentages, not bare numbers: an unregistered custom property cannot be used
      // as a numeric argument (calc multiplier / scaleY), but works as a gradient stop
      this.style.setProperty('--mw-offer-seg1', `${clamp01(f * 2) * 100}%`);
      this.style.setProperty('--mw-offer-seg2', `${clamp01((f - 0.5) * 2) * 100}%`);

      const all = done1 && done2 && done3;
      this.classList.toggle('is-all-unlocked', all);
      if (this.stackText) {
        const alt = this.stackText.dataset.all;
        const def = this.stackText.dataset.default;
        const next = all && alt ? alt : def;
        if (next && this.stackText.innerHTML.trim() !== next) this.stackText.innerHTML = next;
      }
    }

    qtyMessage(remaining, target, current) {
      if (current === 0) return this.highlight(this.text('addAll'), '[count]', target);
      if (remaining <= 0) return this.text('qtyDone').split('[saving]').join('');
      return this.highlight(this.text(remaining === 1 ? 'addOne' : 'addMore'), '[count]', remaining);
    }

    // wrap the live figure so it can be emphasised without hard-coding markup in settings
    highlight(template, token, value) {
      const safe = String(value).replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])
      );
      return template.split(token).join(`<strong class="mw-offer__hl">${safe}</strong>`);
    }

    text(key) {
      const map = {
        unlocked: this.dataset.textUnlocked || 'UNLOCKED!',
        away: this.dataset.textAway || "You're [amount] away!",
        addAll: this.dataset.textAddAll || 'Add [count] qualifying items!',
        addMore: this.dataset.textAddMore || 'Add [count] more items!',
        addOne: this.dataset.textAddOne || 'Add 1 more item!',
        qtyDone: this.dataset.textQtyDone || 'LOWEST-PRICED ITEM',
      };
      return map[key];
    }
  }

  customElements.define('offer-progress', OfferProgress);
}
